"use client";

// Sidebar scope — which question the menu is being sliced by right now.
//
// Until 2026-09-06 the three filter strips (platform, family, folders) were
// stacked and permanently visible, so five rows of chrome came before the
// first menu item and all three filters could be armed at once without the
// reader remembering it. Gal asked for one control instead:
// "אפשר לחלק אותו לאזורים שבעצם הוא יציג בToggle או את כל האפליקציות או את
//  הפלטפורמות שלנו … ואז מתחת לזה עוד אופציה של התיקיות שאני פותח".
//
// One scope is active at a time and only that scope's strip renders. The
// caller clears the filters belonging to the scopes it left — see Sidebar.tsx
// — so a filter can never keep hiding apps from a strip that is off screen.
//
// Four positions, not the three Gal listed: the family filter already exists
// and is the same kind of slice, so folding it in costs no extra row and
// losing it would have been a silent removal.
//
// Persistence: localStorage "cc-sidebar-scope". The module-level subscriber
// set mirrors useSelectedAppFolder next door, so every surface that reads the
// scope moves together in the same tab; `storage` covers other tabs.

import { useCallback, useEffect, useState } from "react";

export type SidebarScope = "all" | "platforms" | "families" | "folders";

export const SCOPE_KEY = "cc-sidebar-scope";

export const SIDEBAR_SCOPES: readonly SidebarScope[] = ["all", "platforms", "families", "folders"];

const subscribers = new Set<() => void>();
function notify(): void {
  for (const fn of subscribers) fn();
}

function read(): SidebarScope {
  if (typeof window === "undefined") return "all";
  try {
    const v = window.localStorage.getItem(SCOPE_KEY);
    return SIDEBAR_SCOPES.includes(v as SidebarScope) ? (v as SidebarScope) : "all";
  } catch {
    return "all";
  }
}

export function useSidebarScope() {
  // Always "all" on the first render — reading localStorage here would make
  // the server and client markup disagree, the same reason folderMode and
  // openFolders hydrate in an effect.
  const [scope, setScopeState] = useState<SidebarScope>("all");

  useEffect(() => {
    setScopeState(read());

    const onChange = () => setScopeState(read());
    subscribers.add(onChange);

    const onStorage = (e: StorageEvent) => {
      if (e.key === SCOPE_KEY) onChange();
    };
    window.addEventListener("storage", onStorage);

    return () => {
      subscribers.delete(onChange);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const setScope = useCallback((next: SidebarScope) => {
    try {
      window.localStorage.setItem(SCOPE_KEY, next);
    } catch {
      // Quota / privacy mode — in-memory state below still updates.
    }
    setScopeState(next);
    notify();
  }, []);

  return { scope, setScope };
}
